<template>
  <div>
    <v-tooltip v-if="props.isDisabled" class="mr-0" top>
      <template #activator="{ on }">
        <span v-on="on">
          <vs-button :label="buttonText" disabled full-width />
        </span>
      </template>
      <Span>{{ props.disabledTooltipText }}</Span>
    </v-tooltip>

    <vs-button
      v-if="!props.isDisabled"
      :label="buttonText"
      :loading="props.isLoading"
      destructive
      full-width
      type="secondary"
      @click="handleClick"
    />
  </div>
</template>

<script async lang="ts" setup>
/**
 * Description
 * This is a component in which a button can be in one of two enabled states as
 * well as in the disabled state. When it is in the disabled state, a tooltip
 * will be displayed when the user hovers over the button according to the
 * disabledTooltipText prop.
 *
 * Props
 *
 * @prop {Boolean} alternate - A boolean property that determines whether the
 *  button should display as an alternate button.
 * @prop {Boolean | null} isDisabled - A boolean property that determines whether
 * the button should display as a disabled button.
 * @prop {Boolean | null} isLoading - A boolean property that determines whether
 * the button should display a loading state.
 * @prop {String | null} disabledTooltipText - A string property that contains
 * the text that should be displayed in the tooltip when the button is disabled.
 * @prop {String} mainButtonText - A string property that contains the text that
 *  should be displayed on the button when it is not an alternate button.
 * @prop {String} alternateButtonText - A string property that contains the text
 *  that should be displayed on the button when it is an alternate button.
 * @prop {Boolean | Null} alternateOnClickHandler - A boolean property that
 * determines whether the alternate button should emit an alternate event when
 * clicked.
 *
 * Emits
 *
 * These should always reverse the current state of the button.
 * @event click - Emitted when the button is clicked.
 * @event alt-click - Emitted when the alternate button is clicked and the
 * `alternateOnClickHandler` prop is set to `true`.
 */

import { computed } from 'vue'

interface Props {
  alternate: boolean
  isDisabled?: boolean | null
  isLoading?: boolean | null
  disabledTooltipText?: string | null
  mainButtonText: string
  alternateButtonText: string
  alternateOnClickHandler?: boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  alternate: false,
  isDisabled: false,
  isLoading: false,
  disabledTooltipText: '',
  mainButtonText: '',
  alternateButtonText: '',
  alternateOnClickHandler: false,
})

const buttonText = computed<string>(() => {
  return props.alternate ? props.alternateButtonText : props.mainButtonText
})

const emits = defineEmits(['click', 'alt-click'])

const handleClick = () => {
  if (props.alternateOnClickHandler && props.alternate) {
    emits('alt-click')
  }
  emits('click')
}
</script>
